import { Gradient, Pattern, util, type FabricObject } from 'fabric';
import type { FillValue } from './types';

/**
 * Resolve a FillValue param into what fabric accepts as `fill`.
 *
 * - string / {type:'solid'}      → color string (unchanged behavior)
 * - {type:'linear-gradient'}     → fabric Gradient with percentage coords
 *   (scales with the object; angle in degrees, 0 = left→right, 90 = top→bottom)
 * - {type:'texture'}             → fabric Pattern (repeat), loaded with
 *   crossOrigin:'anonymous'; REJECTS on a dead URL — callers keep their
 *   existing try/catch + solid-fallback pattern.
 *
 * Stock objects only: Gradient/Pattern are fill VALUES, not object types, so
 * the exportability rule holds — templates stay plain fabric JSON.
 */
export async function resolveFill(fill: FillValue | undefined | null): Promise<
  string | Gradient<'linear'> | Gradient<'radial'> | Pattern | undefined
> {
  if (fill != null && typeof fill === 'object' && fill.type === 'texture') {
    const img = await util.loadImage(fill.url, { crossOrigin: 'anonymous' });
    return new Pattern({
      source: img,
      repeat: 'repeat',
      patternTransform: fill.scale && fill.scale !== 1
        ? [fill.scale, 0, 0, fill.scale, 0, 0]
        : undefined,
    });
  }
  return resolveFillSync(fill);
}

/**
 * Synchronous core of resolveFill for the forms that never load assets:
 * string / solid / linear-gradient / radial-gradient. Textures (the only
 * async form — image decode) return undefined here; use resolveFill.
 *
 * Split out (poster-background lane) so verbs that must paint BEFORE the
 * caller's synchronous history commit (the workspace background: the UI
 * commits immediately after the verb returns) can share the exact gradient
 * construction instead of duplicating it.
 */
export function resolveFillSync(fill: FillValue | undefined | null):
  string | Gradient<'linear'> | Gradient<'radial'> | undefined {
  if (fill == null) return undefined;
  if (typeof fill === 'string') return fill;
  switch (fill.type) {
    case 'solid':
      return fill.color;
    case 'linear-gradient': {
      const angle = ((fill.angle ?? 0) * Math.PI) / 180;
      // Unit-square direction vector for percentage gradientUnits.
      const x2 = 0.5 + Math.cos(angle) / 2;
      const y2 = 0.5 + Math.sin(angle) / 2;
      const stops =
        fill.stops && fill.stops.length >= 2
          ? fill.stops
          : [
              { offset: 0, color: fill.from ?? '#000000' },
              { offset: 1, color: fill.to ?? '#ffffff' },
            ];
      return new Gradient({
        type: 'linear',
        gradientUnits: 'percentage',
        coords: { x1: 1 - x2, y1: 1 - y2, x2, y2 },
        colorStops: stops.map((s) => ({ offset: s.offset, color: s.color })),
      });
    }
    case 'radial-gradient': {
      return new Gradient({
        type: 'radial',
        gradientUnits: 'percentage',
        coords: { x1: 0.5, y1: 0.5, r1: 0, x2: 0.5, y2: 0.5, r2: 0.5 },
        colorStops: fill.stops.map((s) => ({ offset: s.offset, color: s.color })),
      });
    }
    default:
      return undefined;
  }
}

/**
 * Convenience: resolve and assign a fill onto an object, falling back to the
 * provided solid color when the fill can't resolve (dead texture URL etc.).
 */
export async function applyFill(
  obj: FabricObject,
  fill: FillValue | undefined,
  fallback: string,
): Promise<void> {
  try {
    const resolved = await resolveFill(fill);
    obj.set('fill', resolved ?? fallback);
  } catch {
    obj.set('fill', fallback);
  }
}
